import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FadeUp } from '../components/Motion';
import { MapPin, Phone, Mail, Send, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState({ sent: false, error: '', loading: false });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ sent: false, error: '', loading: true });
    try {
      await api.post('/contact', form);
      setStatus({ sent: true, error: '', loading: false });
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : t('contact.error');
      setStatus({ sent: false, error: msg, loading: false });
    }
  };

  return (
    <main data-testid="contact-page" className="pt-32">
      <section className="container-x max-w-7xl mx-auto pb-12">
        <FadeUp>
          <p className="overline">{t('contact.overline')}</p>
          <h1 className="mt-4 font-heading text-5xl sm:text-6xl lg:text-7xl text-ink tracking-tight max-w-4xl">{t('contact.title')}</h1>
          <p className="mt-6 text-lg text-ink-soft max-w-2xl leading-relaxed">{t('contact.intro')}</p>
        </FadeUp>
      </section>

      <section className="container-x max-w-7xl mx-auto pb-24 grid gap-12 lg:grid-cols-12">
        <FadeUp className="lg:col-span-5">
          <div className="bg-forest text-bone rounded-3xl p-8 sm:p-10">
            <h3 className="font-heading text-2xl mb-6 text-bone">{t('contact.info_title')}</h3>
            <ul className="grid gap-5">
              <li className="flex items-start gap-4">
                <MapPin className="text-sun mt-1 shrink-0" size={20} />
                <div>
                  <p className="overline text-sun mb-1">{t('contact.address')}</p>
                  <p className="text-bone/85">{t('contact.address_v')}</p>
                  <p className="text-bone/70 text-sm mt-1">{t('contact.site_v')}</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Phone className="text-sun mt-1 shrink-0" size={20} />
                <div>
                  <p className="overline text-sun mb-1">{t('contact.phone')}</p>
                  <p className="text-bone/85">+226 75 19 21 29</p>
                  <p className="text-bone/85">+226 60 84 58 58</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Mail className="text-sun mt-1 shrink-0" size={20} />
                <div>
                  <p className="overline text-sun mb-1">{t('contact.email_label')}</p>
                  <a href="mailto:lawatanperma@gmail.com" className="text-bone/85 hover:text-sun">lawatanperma@gmail.com</a>
                </div>
              </li>
            </ul>
          </div>
          <div className="mt-6 rounded-3xl overflow-hidden border border-border-soft">
            <iframe
              title="Lawatan Map"
              src="https://www.openstreetmap.org/export/embed.html?bbox=-4.45,11.05,-4.05,11.30&amp;layer=mapnik&amp;marker=11.1771,-4.2979"
              className="w-full h-72"
              loading="lazy"
            />
          </div>
        </FadeUp>

        <FadeUp delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} className="bg-white border border-border-soft rounded-3xl p-8 sm:p-10 grid gap-5" data-testid="contact-form">
            <div className="grid gap-2">
              <label className="overline">{t('contact.name')}</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="contact-name"
                className="rounded-xl border border-border-soft bg-bone px-4 py-3 outline-none focus:border-forest transition"
              />
            </div>
            <div className="grid gap-2 sm:grid-cols-2 sm:gap-4">
              <div className="grid gap-2">
                <label className="overline">{t('contact.email')}</label>
                <input
                  required type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-email"
                  className="rounded-xl border border-border-soft bg-bone px-4 py-3 outline-none focus:border-forest transition"
                />
              </div>
              <div className="grid gap-2">
                <label className="overline">{t('contact.subject')}</label>
                <input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  data-testid="contact-subject"
                  className="rounded-xl border border-border-soft bg-bone px-4 py-3 outline-none focus:border-forest transition"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <label className="overline">{t('contact.message')}</label>
              <textarea
                required rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-message"
                className="rounded-xl border border-border-soft bg-bone px-4 py-3 outline-none focus:border-forest transition resize-none"
              />
            </div>
            {status.sent && (
              <div className="flex items-center gap-2 text-forest text-sm" data-testid="contact-success">
                <CheckCircle2 size={18} /> {t('contact.sent')}
              </div>
            )}
            {status.error && <p className="text-red-700 text-sm" data-testid="contact-error">{status.error}</p>}
            <button type="submit" disabled={status.loading} className="btn-primary self-start mt-2 disabled:opacity-60" data-testid="contact-submit">
              {status.loading ? t('common.loading') : (<>{t('contact.send')} <Send size={16} /></>)}
            </button>
          </form>
        </FadeUp>
      </section>
    </main>
  );
}
